import { CellClickedEvent, GridApi, ColDef, RowSelectionOptions, GridOptions, RowClickedEvent } from "ag-grid-community";

import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { forkJoin, Observable, Subscription, of } from "rxjs";
import { concatMap, tap, switchMap, map } from "rxjs/operators";
import { deepClone } from "../../../../builder/models/editor.model";
import { LunaEntity } from "../../../../builder/models/entity.model";
import { EntityBuilderService } from "../../../../builder/services/entity-builder.service";
import { CheckboxRendererComponent } from "../../../../common/components/ag-grid/checkbox-renderer.component";
import { AgGridBooleanFilterComponent, IBooleanFilterParams } from "../../../../common/components/ag-grid/filters/boolean-filter.component";
import { IconActionRendererComponent } from "../../../../common/components/ag-grid/icon-action-renderer.component";
import { OnBaseDocumentGroup, OnBaseDocumentType, OnBaseKeyword, OnBaseKeywordMappingSearchSpec, OnBaseMappedDocumentGroup, OnBaseMappedKeyword, OnBaseProgramArea } from "../../../../common/models/onBaseIntegrationAndMapping.model";
import { DocumentManagementService } from "../../../../common/services/document-management.service";
import { GridFormatterService } from "../../../../common/services/grid-formatter.service";
import { LoaderService } from "../../../../common/services/loader.service";
import { OnBaseIntegrationAndMappingService } from "../../../../common/services/onbase-integration-mapping.service";
import { Dialog, DialogButton } from "../../../../core/models/dialog.model";
import { SubHeader } from "../../../../core/models/sub-header.model";
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { SubHeaderService } from "../../../../core/services/sub-header.service";
import { AuthService } from "../../../../services/auth.service";

@Component({
    selector: 'onbase-interface-config-component',
    templateUrl: './onbase-interface-config-component.html',
    styleUrls: ['./onbase-interface-config-component.scss'],
    standalone: false
})
export class OnBaseInterfaceConfigComponent implements OnInit, OnDestroy {

    errorDialog: Dialog = new Dialog("An error has occurred", [new DialogButton("OK", false, () => { return; })]);
    updateMappingErrorDialog: Dialog = new Dialog("Please select a valid Document Group, Document Type and Keyword combination", [new DialogButton("OK", false, () => { return; })]);

	programAreas: OnBaseProgramArea[] = [];
	programAreaKeyword?: OnBaseKeyword = undefined;
    currentlySelectedGridProgramArea: OnBaseProgramArea = {} as OnBaseProgramArea;
    documentGroups: OnBaseDocumentGroup[] = [];
    selectedDocumentGroupsForProgramArea: OnBaseDocumentGroup[] = [];

    lastCellClickedEvent: CellClickedEvent;
    public mappedKeywords: OnBaseMappedKeyword[] = [];
	public programAreaGridOptions: GridOptions;
	private gridApi?: GridApi;
    _booleanfilterParams = { isInServerSideFilterMode: false } as IBooleanFilterParams;

    _columns: ColDef[] = [];
    _programAreaColumns: ColDef[] = [];

    newProgramAreaName = "";
    programAreasFetchedAtLeastOnce = false;
    documentGroupsFetchedAtLeastOnce = false;
    loadingKeywords = false;

    header: SubHeader = new SubHeader({ title: "OnBase Integration Config", buttons: [] });

    subscription: Subscription = new Subscription();

    filterModel: any = {};

    deletedGroupsMessage: string = "";
    processingResults: string[] = [];
    processingStatus: string = "Not Started";
    onBaseGroupsStatus: string = "Not Started";
    onBaseGroups: OnBaseDocumentGroup[] = [];


    constructor(public router: Router, private _subheaderSvc: SubHeaderService, private loader: LoaderService, private _documentService: DocumentManagementService, private _entityService: EntityBuilderService,
        private _gridFormatterService: GridFormatterService, private _mappingService: OnBaseIntegrationAndMappingService, private _dialogService: DynamicDialogService, public auth: AuthService) {
		this.buildProgramAreaColumns();
    }

    ngOnInit() {
        this._subheaderSvc.updateSubHeader(this.header);
        this.loader.toggleLoader(true);

        this.programAreaGridOptions = this._gridFormatterService.BuildDefaultGridOptionsWithColumns(this._programAreaColumns);
        this.programAreaGridOptions.context = {
            componentParent: this
        };
        this.programAreaGridOptions.rowSelection = {
            mode: "singleRow",
            checkboxes: false
        } as RowSelectionOptions;
        this.programAreaGridOptions.getRowStyle = function (params) {
            if (params.data && params.context.currentlySelectedGridProgramArea && params.data.id === params.context.currentlySelectedGridProgramArea.id) {
                return { border: '2px solid #b63e2e', background: 'blue', }
            }
            return undefined;
        }
        this.programAreaGridOptions.onRowClicked = this.onProgramAreaRowClicked;
		this.programAreaGridOptions.onGridReady = (params) => { 
            this.gridApi = params.api; 
            this.gridApi.sizeColumnsToFit();
        };
		let programKeywordObs$: Observable<OnBaseKeyword | undefined> = this._mappingService.GetAllKeywords().pipe(
			map((keywords: OnBaseKeyword[]) => {
				return keywords.find(k => k.name.toLocaleLowerCase().includes('program area'));
			}),
			tap((keyword: OnBaseKeyword | undefined) => {
				this.programAreaKeyword = keyword;
			})
		);

		let programAreasObs$ = this._documentService.getAllProgramAreas().pipe(tap((programAreas: OnBaseProgramArea[]) => {
			this.programAreas = programAreas;
			this.programAreasFetchedAtLeastOnce = true;
			this.buildProgramAreaColumns();
			if (this.gridApi) {
                this.gridApi.setGridOption("columnDefs", this._programAreaColumns);
            }
			
		}));

		let programAreaSubs$ = programKeywordObs$.pipe(
			switchMap((keyword: OnBaseKeyword | undefined) => {
				console.log('received keyword', keyword);
				return programAreasObs$;
			})
		).subscribe({
            next: () => { this.loader.toggleLoader(false); },
			error: (error) => {
				this.loader.toggleLoader(false);
				console.error(error);
				this.handleError(error);
			}
        }
		);

		this.subscription.add(programAreaSubs$);
        let documentGroupsObs$ = this._documentService.getAllMappedDocumentGroups().pipe(tap((documentGroups: OnBaseMappedDocumentGroup[]) => {
            this.documentGroups = this.documentGroups.concat(documentGroups);
        })).subscribe({
            next: () => {
                this.documentGroupsFetchedAtLeastOnce = true;
                //If they've already selected a program area, make sure to set the existing groups once they come back
                if (this.currentlySelectedGridProgramArea && this.currentlySelectedGridProgramArea.id != null)
                    this.selectedDocumentGroupsForProgramArea = this.documentGroups.filter(a => a.programAreaId == this.currentlySelectedGridProgramArea.id)

                this.loader.toggleLoader(false);
            },
            error: (error) => {
                this.loader.toggleLoader(false)
                console.error(error);
                this.handleError(error);
            }
        }
        );
        this.subscription.add(documentGroupsObs$);
    }

    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
    }

    handleError(error: any): void {
        this.errorDialog.toggleDialog(true);
        this._dialogService.updateDialog(this.errorDialog);
    }

    home(): void {
        this.router.navigate(["/home"]);
    }

    buildProgramAreaColumns() {
        this._programAreaColumns = [
            {
                headerName: 'Program Area Id',
                headerTooltip: 'Program Area Id',
                field: 'id',
                tooltipField: 'id',
                filter: "agTextColumnFilter",
                filterParams: this._gridFormatterService.defaultTextFilterParams,
                minWidth: 200,
                maxWidth: 250
            },
            {
                headerName: 'Program Area Name',
                headerTooltip: 'Program Area Name',
                field: 'name',
                tooltipField: 'name',
                filter: "agTextColumnFilter",
                filterParams: this._gridFormatterService.defaultTextFilterParams,
                minWidth: 420
            }
        ];
    }

    canAddProgramArea(): boolean {
        let canAddProgramArea = this.programAreasFetchedAtLeastOnce && this.newProgramAreaName != "" && this.programAreas.filter(a => a.name == this.newProgramAreaName).length == 0;

        return canAddProgramArea;
    }

    addProgramArea() {
        this.loader.toggleLoader(true);
        let newProgramAreaSaveObs$ = this._documentService.saveProgramArea({ name: this.newProgramAreaName } as OnBaseProgramArea).pipe(concatMap((programAreaId: number) => {
            return this._documentService.getAllProgramAreas();
        })).subscribe({
            next: (programAreas: OnBaseProgramArea[]) => {
                this.loader.toggleLoader(false);
                this.programAreas = programAreas;
            },
            error: (error) => {
                this.loader.toggleLoader(false);
                console.error(error);
                this.handleError(error);
            }
        }
        );
        this.subscription.add(newProgramAreaSaveObs$);
    }

    programAreaHasBeenSelected(): boolean {
        return this.currentlySelectedGridProgramArea && this.currentlySelectedGridProgramArea.id != undefined && this.currentlySelectedGridProgramArea.id != null;
    }

    canUpdateProgramArea(): boolean {
        let canUpdateProgramArea = this.programAreaHasBeenSelected() && this.currentlySelectedGridProgramArea.name != "" && this.currentlySelectedGridProgramArea.name != undefined
                                    && this.programAreas.filter(a => a.name == this.currentlySelectedGridProgramArea.name && a.id != this.currentlySelectedGridProgramArea.id).length == 0;

        return canUpdateProgramArea;
	}

	programAreaUnderMaximumLength(): boolean {
		if (this.programAreaKeyword == null || this.programAreaKeyword.dataLength == null) return true;
		if (this.programAreaHasBeenSelected()) {
			return this.currentlySelectedGridProgramArea.name.length <= this.programAreaKeyword.dataLength;
		}
		else {
			if (this.newProgramAreaName == null) return true;
			return this.newProgramAreaName.length <= this.programAreaKeyword.dataLength;
		}
		
	}

    onTabClicked() {
    }

    updateProgramArea() {
        this.loader.toggleLoader(true);
        let updatedProgramAreaSaveObs$ = this._documentService.saveProgramArea(this.currentlySelectedGridProgramArea as OnBaseProgramArea).pipe(concatMap((programAreaId: number) => {

            let allSaveDocumentGroupCalls$: Observable<boolean> [] =[];
            for (let x = 0; x < this.selectedDocumentGroupsForProgramArea.length; x++) {
                this.selectedDocumentGroupsForProgramArea[x].programAreaId = this.currentlySelectedGridProgramArea.id;
                let saveDocumentGroup$ = this._documentService.saveDocumentGroup(this.selectedDocumentGroupsForProgramArea[x]).pipe(tap((sucess) => {
                    return sucess;
                }));
                allSaveDocumentGroupCalls$.push(saveDocumentGroup$);
            }

            return forkJoin(allSaveDocumentGroupCalls$).pipe(concatMap((results) => {
                return this._documentService.getAllProgramAreas().pipe(concatMap((resultingProgramAreas) => {
                    this.programAreas = resultingProgramAreas;
                    return this._documentService.getAllMappedDocumentGroups();
                }));;
            }));

        })).subscribe({
            next: (documentGroups: OnBaseMappedDocumentGroup[]) => {
                this.loader.toggleLoader(false);
                this.documentGroups = documentGroups;
            },
            error: (error) => {
                this.loader.toggleLoader(false);
                console.error(error);
                this.handleError(error);
            }
        }
        );
        this.subscription.add(updatedProgramAreaSaveObs$);
    }

    onProgramAreaRowClicked(event: RowClickedEvent) {
        let parent = event.context.componentParent as OnBaseInterfaceConfigComponent;
        parent.currentlySelectedGridProgramArea = deepClone(event.data) as OnBaseProgramArea;
        if (parent.documentGroupsFetchedAtLeastOnce) {
            parent.selectedDocumentGroupsForProgramArea = parent.documentGroups.filter(a => a.programAreaId == event.data.id)
        }
        else {
            //THrow up the loader, that will be closed by the get document groups method returning
            parent.loader.toggleLoader(true);
        }
    }

    updateProgramAreaDocumentGroups(currentlySelectedDocumentGroups: OnBaseDocumentGroup[]) {
        this.selectedDocumentGroupsForProgramArea = currentlySelectedDocumentGroups;
    }

    importFromOnBase() {
        this.loader.toggleLoader(true);
        let newProgramAreaSaveObs$ = this._mappingService.importOnBaseMappings().subscribe((success: boolean) => {
            this.loader.toggleLoader(false);
        },
            (error) => {
                this.loader.toggleLoader(false);
                console.error(error);
                this.handleError(error);
            }
        );
        this.subscription.add(newProgramAreaSaveObs$);
    }

    loadOnBaseGroups(): void {
        this.onBaseGroupsStatus = 'Loading...';
        let obs$ = this._documentService.getAllDocumentGroups()
            .pipe(tap((groups: OnBaseDocumentGroup[]) => {
                this.onBaseGroupsStatus = "Complete";
                this.onBaseGroups = groups;
            })).subscribe({
                next: () => { },
                error: (error) => { console.error(error); }
        });
        this.subscription.add(obs$);
    }

    processOnBaseGroups(): void {
        if (this.onBaseGroups.length > 0) {
            this.processingResults = [];
            this.deletedGroupsMessage = "";
            this.processingStatus = `Processing ${this.onBaseGroups[0].name}`;
            let obs$: Observable<string> = this._mappingService.runGroupMapping(this.onBaseGroups[0]);
            for (let i = 0; i < this.onBaseGroups.length; i++) {
                obs$ = obs$.pipe(
                    switchMap((previousResult: string) => {
                        this.processingResults.push(previousResult);
                        if (i < this.onBaseGroups.length - 1) {
                            this.processingStatus = `Processing ${this.onBaseGroups[i + 1].name}`;
                            return this._mappingService.runGroupMapping(this.onBaseGroups[i + 1])
                        } else {
                            return this._mappingService.deleteDiscontinuedGroups(this.onBaseGroups).pipe(
                                map((deletedCount: number) => {
                                    this.deletedGroupsMessage = `Deleted ${deletedCount} no longer used groups`;
                                    return 'Processing Complete';
                                })
                            )
                        }
                    })
                );               
            }
            let subs$ = obs$.subscribe({
                next: (result: string) => {
                    this.processingStatus = result;
                },
                error: (error) => {
                    this.processingStatus = "Error Occurred";
                    console.error(error);
                }
            });
            this.subscription.add(subs$);
        }
    }
}